'use client';

import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from './Toast';
import { ToasterToast, useToast } from './useToast';

import { createContext, useContext, useMemo, useState } from 'react';

export enum ToastView {
  Normal = 'normal',
  Map = 'map',
}

interface ToastContextProps {
  toastView: ToastView;
  setToastView: (toastView: ToastView) => void;
}

export const ToastContext = createContext<ToastContextProps | undefined>(
  undefined
);

export function Toaster() {
  const { toasts } = useToast();

  return (
    <ToastProvider>
      {toasts.map(function ({ id, title, description, action, ...props }) {
        return (
          <Toast key={id} {...props}>
            <div className="grid gap-1">
              {title && <ToastTitle>{title}</ToastTitle>}
              {description && (
                <ToastDescription>{description}</ToastDescription>
              )}
            </div>
            {action}
            <ToastClose />
          </Toast>
        );
      })}
      <ToastViewport />
    </ToastProvider>
  );
}

interface ToasterInDialogProps {
  toasts: ToasterToast[];
  children: React.ReactNode;
}

export function ToasterInDialog({ toasts, children }: ToasterInDialogProps) {
  return (
    <ToastProvider>
      {toasts.map(function ({ id, title, description, action, ...props }) {
        return (
          <Toast key={id} {...props}>
            <div className="grid gap-1">
              {title && <ToastTitle>{title}</ToastTitle>}
              {description && (
                <ToastDescription>{description}</ToastDescription>
              )}
            </div>
            {action}
            <ToastClose />
          </Toast>
        );
      })}
      <ToastViewport />
      {children}
    </ToastProvider>
  );
}

interface ToasterWithViewStateProps {
  children: React.ReactNode;
}

export function ToasterWithViewState({ children }: ToasterWithViewStateProps) {
  const { toasts } = useToast();

  const [toastView, setToastView] = useState<ToastView>(ToastView.Normal);

  const contextValue = useMemo(
    () => ({
      toastView,
      setToastView,
    }),
    [toastView, setToastView]
  );

  return (
    <ToastProvider>
      {toasts.map(function ({ id, title, description, action, ...props }) {
        return (
          <Toast key={id} {...props} className="w-[388px] h-[94px] z-[9999]">
            <div className="grid gap-1">
              {title && <ToastTitle>{title}</ToastTitle>}
              {description && (
                <ToastDescription>{description}</ToastDescription>
              )}
            </div>
            {action}
            <ToastClose />
          </Toast>
        );
      })}
      {toastView === ToastView.Normal && <ToastViewport />}
      <ToastContext.Provider value={contextValue}>
        {children}
      </ToastContext.Provider>
    </ToastProvider>
  );
}

export const useToastContext = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error(
      'useToastContext must be used within a ToasterWithViewState'
    );
  }
  return context;
};
